import { FC, useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  Box,
  VStack,
  Heading,
  Input,
  Button,
  Text,
  HStack,
  Textarea,
  NativeSelectRoot,
  NativeSelectField,
  Badge,
} from '@chakra-ui/react';
import { useDogStore } from '@/stores/dogStore';
import { useAuthStore } from '@/stores/authStore';
import { AppLayout } from '@/components/layout/AppLayout';
import { dogService } from '@/services/firebase/dogService';
import { documentService } from '@/services/firebase/documentService';
import { DogFormData, GeneticTestResult } from '@/types/dog';
import { PhotoUploader } from '@/components/dog/PhotoUploader';
import { PhotoGallery } from '@/components/dog/PhotoGallery';
import { PDFAnalyzer } from '@/components/dog/PDFAnalyzer';
import { AnalysisResultPreview } from '@/components/dog/AnalysisResultPreview';
import { ParentDogSelector } from '@/components/dog/ParentDogSelector';
import { VaccinationSection } from '@/components/vaccination/VaccinationSection';
import { usePDFAnalysisStore, isPedigreeData, isMicrochipData, isGeneticTestData, isUnifiedData } from '@/stores/pdfAnalysisStore';
import {
  ExtractedPedigreeData,
  ExtractedMicrochipData,
  ExtractedGeneticTestData,
  ExtractedUnifiedData,
  GENETIC_TEST_RESULT_LABELS,
  GENETIC_TEST_RESULT_COLORS,
} from '@/types/pdfAnalysis';
import { FiX } from 'react-icons/fi';
import { findParentDogMatch, ParentMatchResult } from '@/utils/parentDogMatcher';
import { registerGrandparents } from '@/services/dog/grandparentRegistration';
import { toaster } from '@/components/ui/toaster';

/**
 * 個体登録・編集ページコンポーネント（仮実装）
 */
/**
 * 拡張フォームデータ型（日付文字列を含む）
 */
interface ExtendedDogFormData extends DogFormData {
  birthDateStr: string;
  // 祖父母セレクタの一時値（既存個体 ID または `__new__:名前`）。
  // Dog/DogFormData の永続フィールドではなく、送信時に父犬・母犬へ紐付ける。
  paternalGrandfatherId?: string;
  paternalGrandmotherId?: string;
  maternalGrandfatherId?: string;
  maternalGrandmotherId?: string;
}

const DogFormPage: FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuthStore();
  const { selectedDog, dogs, fetchDog, fetchDogs } = useDogStore();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedDogId, setSavedDogId] = useState<string | null>(null);
  const [showPhotoSection, setShowPhotoSection] = useState(false);
  const [geneticTestResults, setGeneticTestResults] = useState<GeneticTestResult[]>([]);
  const [fatherMatchResult, setFatherMatchResult] = useState<ParentMatchResult | null>(null);
  const [motherMatchResult, setMotherMatchResult] = useState<ParentMatchResult | null>(null);
  // 祖父母 4 個体の AI マッチング結果（バッジ表示用）
  const [paternalGrandfatherMatchResult, setPaternalGrandfatherMatchResult] =
    useState<ParentMatchResult | null>(null);
  const [paternalGrandmotherMatchResult, setPaternalGrandmotherMatchResult] =
    useState<ParentMatchResult | null>(null);
  const [maternalGrandfatherMatchResult, setMaternalGrandfatherMatchResult] =
    useState<ParentMatchResult | null>(null);
  const [maternalGrandmotherMatchResult, setMaternalGrandmotherMatchResult] =
    useState<ParentMatchResult | null>(null);

  const { reset: resetPDFAnalysis, selectedFile: pdfFile } = usePDFAnalysisStore();

  const isEditMode = !!id;

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<ExtendedDogFormData>();

  // 親犬IDをwatchで取得
  const fatherId = watch('fatherId');
  const motherId = watch('motherId');
  // 祖父母セレクタ値を watch で取得
  const paternalGrandfatherId = watch('paternalGrandfatherId');
  const paternalGrandmotherId = watch('paternalGrandmotherId');
  const maternalGrandfatherId = watch('maternalGrandfatherId');
  const maternalGrandmotherId = watch('maternalGrandmotherId');

  // 親犬選択用の個体一覧を取得
  useEffect(() => {
    if (user) {
      fetchDogs(user.uid);
    }
  }, [user, fetchDogs]);

  useEffect(() => {
    if (isEditMode && user && id) {
      fetchDog(id, user.uid);
      setSavedDogId(id); // 編集モード時は初期表示でsavedDogIdを設定
    }
  }, [isEditMode, user, id, fetchDog]);

  useEffect(() => {
    if (selectedDog && isEditMode) {
      // フォームに既存データをセット
      setValue('bioNumber', selectedDog.bioNumber);
      setValue('name', selectedDog.name);
      setValue('gender', selectedDog.gender);
      setValue(
        'birthDateStr',
        selectedDog.birthDate.toDate().toISOString().split('T')[0]
      );
      setValue('fatherId', selectedDog.fatherId || '');
      setValue('motherId', selectedDog.motherId || '');
      setValue('status', selectedDog.status);
      setValue('notes', selectedDog.notes || '');
      setValue('characteristics', selectedDog.characteristics);
      // 新規フィールド
      setValue('breed', selectedDog.breed || '');
      setValue('officialName', selectedDog.officialName || '');
      setValue('microchipNumber', selectedDog.microchipNumber || '');
      setValue('microchipPin', selectedDog.microchipPin || '');
      setValue('jkcRegistrationNumber', selectedDog.jkcRegistrationNumber || '');
      setValue('dnaRegistrationNumber', selectedDog.dnaRegistrationNumber || '');
      setValue('breederName', selectedDog.breederName || '');
      setValue('ownerName', selectedDog.ownerName || '');
      if (selectedDog.geneticTestResults) {
        setGeneticTestResults(selectedDog.geneticTestResults);
      }

      // 祖父母のプレフィル: 父犬/母犬の fatherId/motherId（=祖父母）を辿って復元
      const father = dogs.find((d) => d.id === selectedDog.fatherId);
      setValue('paternalGrandfatherId', father?.fatherId || '');
      setValue('paternalGrandmotherId', father?.motherId || '');
      const mother = dogs.find((d) => d.id === selectedDog.motherId);
      setValue('maternalGrandfatherId', mother?.fatherId || '');
      setValue('maternalGrandmotherId', mother?.motherId || '');
    }
  }, [selectedDog, isEditMode, setValue, dogs]);

  // コンポーネントのアンマウント時にPDF解析状態をリセット
  useEffect(() => {
    return () => {
      resetPDFAnalysis();
    };
  }, [resetPDFAnalysis]);

  /**
   * 親犬マッチング処理を実行
   */
  const processParentMatching = useCallback(
    (ancestors: ExtractedPedigreeData['ancestors']) => {
      if (!ancestors) return;

      /**
       * 1 個体分のマッチングを実行し、フォーム値と matchResult を更新する。
       * - ヒット → ID を setValue
       * - 未ヒットかつ名前あり → `__new__:名前` を setValue
       */
      const matchAndSet = (
        info: { name?: string; registrationNumber?: string } | undefined,
        gender: 'male' | 'female',
        field:
          | 'fatherId'
          | 'motherId'
          | 'paternalGrandfatherId'
          | 'paternalGrandmotherId'
          | 'maternalGrandfatherId'
          | 'maternalGrandmotherId',
        setMatchResult: (result: ParentMatchResult) => void
      ) => {
        if (!info || (!info.name && !info.registrationNumber)) return;

        const match = findParentDogMatch(
          { name: info.name, registrationNumber: info.registrationNumber },
          dogs,
          gender
        );
        setMatchResult(match);
        if (match.matchedDogId) {
          setValue(field, match.matchedDogId);
        } else if (info.name) {
          // マッチなしの場合、新規作成オプションを設定
          setValue(field, `__new__:${info.name}`);
        }
      };

      // 父犬・母犬
      matchAndSet(ancestors.father, 'male', 'fatherId', setFatherMatchResult);
      matchAndSet(ancestors.mother, 'female', 'motherId', setMotherMatchResult);

      // 父方祖父母（父犬直下）
      matchAndSet(
        ancestors.paternalGrandfather,
        'male',
        'paternalGrandfatherId',
        setPaternalGrandfatherMatchResult
      );
      matchAndSet(
        ancestors.paternalGrandmother,
        'female',
        'paternalGrandmotherId',
        setPaternalGrandmotherMatchResult
      );

      // 母方祖父母（母犬直下）
      matchAndSet(
        ancestors.maternalGrandfather,
        'male',
        'maternalGrandfatherId',
        setMaternalGrandfatherMatchResult
      );
      matchAndSet(
        ancestors.maternalGrandmother,
        'female',
        'maternalGrandmotherId',
        setMaternalGrandmotherMatchResult
      );
    },
    [dogs, setValue]
  );

  /**
   * PDF解析結果をフォームに反映
   */
  const handleApplyAnalysisResult = useCallback(
    (data: ExtractedPedigreeData | ExtractedMicrochipData | ExtractedGeneticTestData | ExtractedUnifiedData) => {
      // 統合データを最初にチェック（isPedigreeDataより優先）
      if (isUnifiedData(data)) {
        // 血統書フィールド
        if (data.officialName) setValue('officialName', data.officialName);
        if (data.breed) setValue('breed', data.breed);
        if (data.jkcRegistrationNumber) setValue('jkcRegistrationNumber', data.jkcRegistrationNumber);
        if (data.dnaRegistrationNumber) setValue('dnaRegistrationNumber', data.dnaRegistrationNumber);
        if (data.gender) setValue('gender', data.gender);
        if (data.birthDate) setValue('birthDateStr', data.birthDate);
        if (data.color) setValue('characteristics.color', data.color);
        if (data.breederName) setValue('breederName', data.breederName);
        if (data.ownerName) setValue('ownerName', data.ownerName);

        // マイクロチップフィールド
        if (data.microchipNumber) setValue('microchipNumber', data.microchipNumber);
        if (data.microchipPin) setValue('microchipPin', data.microchipPin);

        // 遺伝子検査フィールド
        if (data.animalName) setValue('name', data.animalName);
        if (data.geneticTestResults && data.geneticTestResults.length > 0) {
          const results: GeneticTestResult[] = data.geneticTestResults.map((item) => ({
            testName: item.testName,
            result: item.result,
            laboratory: data.geneticTestLaboratory,
          }));
          setGeneticTestResults((prev) => [...prev, ...results]);
        }

        // 親犬マッチング処理
        if (data.ancestors) {
          processParentMatching(data.ancestors);
        }
      } else if (isPedigreeData(data)) {
        // 血統書データ
        if (data.officialName) setValue('officialName', data.officialName);
        if (data.breed) setValue('breed', data.breed);
        if (data.jkcRegistrationNumber) setValue('jkcRegistrationNumber', data.jkcRegistrationNumber);
        if (data.dnaRegistrationNumber) setValue('dnaRegistrationNumber', data.dnaRegistrationNumber);
        if (data.gender) setValue('gender', data.gender);
        if (data.birthDate) setValue('birthDateStr', data.birthDate);
        if (data.color) {
          setValue('characteristics.color', data.color);
        }
        if (data.breederName) setValue('breederName', data.breederName);
        if (data.ownerName) setValue('ownerName', data.ownerName);

        // 親犬マッチング処理
        if (data.ancestors) {
          processParentMatching(data.ancestors);
        }
      } else if (isMicrochipData(data)) {
        // マイクロチップデータ
        if (data.microchipNumber) setValue('microchipNumber', data.microchipNumber);
        if (data.pin) setValue('microchipPin', data.pin);
      } else if (isGeneticTestData(data)) {
        // 遺伝子検査データ
        if (data.animalName) setValue('name', data.animalName);
        if (data.testResults.length > 0) {
          const results: GeneticTestResult[] = data.testResults.map((item) => ({
            testName: item.testName,
            result: item.result,
            laboratory: data.laboratory,
          }));
          setGeneticTestResults((prev) => [...prev, ...results]);
        }
      }
    },
    [setValue, setGeneticTestResults, processParentMatching]
  );

  /**
   * 遺伝子検査結果を削除
   */
  const handleRemoveGeneticTestResult = (index: number) => {
    setGeneticTestResults((prev) => prev.filter((_, i) => i !== index));
  };

  /**
   * フォーム送信処理
   */
  const onSubmit = async (data: ExtendedDogFormData) => {
    if (!user) return;

    setIsLoading(true);
    setError(null);

    try {
      let actualFatherId = data.fatherId;
      let actualMotherId = data.motherId;

      // 新規父犬の作成（__new__: プレフィックスがある場合）
      if (data.fatherId?.startsWith('__new__:')) {
        const fatherName = data.fatherId.replace('__new__:', '');
        const newFather = await dogService.createDog(
          {
            name: fatherName,
            gender: 'male',
            birthDate: new Date(),
            status: 'active',
            bioNumber: '',
          },
          user.uid
        );
        actualFatherId = newFather.id;
      }

      // 新規母犬の作成（__new__: プレフィックスがある場合）
      if (data.motherId?.startsWith('__new__:')) {
        const motherName = data.motherId.replace('__new__:', '');
        const newMother = await dogService.createDog(
          {
            name: motherName,
            gender: 'female',
            birthDate: new Date(),
            status: 'active',
            bioNumber: '',
          },
          user.uid
        );
        actualMotherId = newMother.id;
      }

      // 祖父母の紐付け処理（フォームの祖父母セレクタ値を起点に解決）
      const hasGrandparentInput =
        !!data.paternalGrandfatherId ||
        !!data.paternalGrandmotherId ||
        !!data.maternalGrandfatherId ||
        !!data.maternalGrandmotherId;
      if (hasGrandparentInput) {
        try {
          await registerGrandparents({
            fatherId: actualFatherId,
            motherId: actualMotherId,
            grandparents: {
              paternalGrandfatherId: data.paternalGrandfatherId,
              paternalGrandmotherId: data.paternalGrandmotherId,
              maternalGrandfatherId: data.maternalGrandfatherId,
              maternalGrandmotherId: data.maternalGrandmotherId,
            },
            userId: user.uid,
          });
        } catch (grandparentError) {
          console.error('祖父母の紐付けエラー:', grandparentError);
          toaster.create({
            title: '祖父母の紐付けに一部失敗しました',
            description: '個体情報は引き続き保存されます',
            type: 'warning',
          });
        }
      }

      // 祖父母セレクタ値は Dog の永続フィールドではないため除外してから保存する
      const {
        paternalGrandfatherId: _pgf,
        paternalGrandmotherId: _pgm,
        maternalGrandfatherId: _mgf,
        maternalGrandmotherId: _mgm,
        ...persistableData
      } = data;
      void _pgf;
      void _pgm;
      void _mgf;
      void _mgm;

      const formData: DogFormData = {
        ...persistableData,
        birthDate: new Date(data.birthDateStr),
        fatherId: actualFatherId,
        motherId: actualMotherId,
        geneticTestResults: geneticTestResults.length > 0 ? geneticTestResults : undefined,
      };

      if (isEditMode && id) {
        // 更新
        await dogService.updateDog(id, formData, user.uid);

        // PDF保存
        if (pdfFile) {
          try {
            await documentService.uploadDocumentWithMetadata(pdfFile, id, user.uid);
          } catch (uploadError) {
            console.error('PDFアップロードエラー:', uploadError);
            toaster.create({
              title: 'PDFの保存に失敗しました',
              description: '個体情報は正常に更新されています',
              type: 'warning',
            });
          }
        }

        setSavedDogId(id);
        setShowPhotoSection(true);
        toaster.success({
          title: '個体情報を保存しました',
          description: '続けて写真をアップロードできます',
        });
      } else {
        // 新規登録
        const newDog = await dogService.createDog(formData, user.uid);

        // PDF保存
        if (pdfFile) {
          try {
            await documentService.uploadDocumentWithMetadata(pdfFile, newDog.id, user.uid);
          } catch (uploadError) {
            console.error('PDFアップロードエラー:', uploadError);
            toaster.create({
              title: 'PDFの保存に失敗しました',
              description: '個体情報は正常に登録されています',
              type: 'warning',
            });
          }
        }

        navigate('/dogs');
        toaster.success({
          title: '個体情報を登録しました',
        });
      }

      // 親犬・祖父母が新規作成された場合、犬一覧を再取得
      const anyNewAncestor = [
        data.fatherId,
        data.motherId,
        data.paternalGrandfatherId,
        data.paternalGrandmotherId,
        data.maternalGrandfatherId,
        data.maternalGrandmotherId,
      ].some((v) => v?.startsWith('__new__:'));
      if (anyNewAncestor) {
        await fetchDogs(user.uid);
      }
    } catch (err) {
      console.error('個体登録・更新エラー:', err);
      setError(err instanceof Error ? err.message : '保存に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 写真アップロード完了後の処理
   */
  const handlePhotoUploadComplete = () => {
    toaster.success({
      title: '登録が完了しました',
      description: '個体情報が正常に保存されました',
      duration: 4000,
    });
    // 個体一覧ページへ遷移
    navigate('/dogs');
  };

  return (
    <AppLayout>
      <Box maxW="4xl" mx="auto">
        <VStack gap={6} align="stretch">
          {/* ヘッダー */}
          <HStack justify="space-between" flexWrap="wrap" gap={4}>
            <Heading size="xl">{isEditMode ? '個体編集' : '個体新規登録'}</Heading>
            <HStack gap={2}>
              <Button variant="outline" onClick={() => navigate('/dogs')}>
                キャンセル
              </Button>
            </HStack>
          </HStack>

        {/* エラー表示 */}
        {error && (
          <Box p={4} bg="red.50" borderRadius="md" color="red.700">
            <Text fontSize="sm">{error}</Text>
          </Box>
        )}

        {/* PDF解析セクション */}
        {user && (
          <PDFAnalyzer userId={user.uid} />
        )}

        {/* PDF解析結果プレビューモーダル */}
        <AnalysisResultPreview
          onApply={handleApplyAnalysisResult}
          onCancel={() => {}}
        />

        {/* フォーム */}
        <Box bg="white" p={8} borderRadius="lg" shadow="md">
          <form onSubmit={handleSubmit(onSubmit)}>
            <VStack gap={6} align="stretch">
              {/* 基本情報 */}
              <Heading size="md">基本情報</Heading>

              {/* 生体番号 */}
              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={2}>
                  生体番号（任意）
                </Text>
                <Input
                  type="text"
                  placeholder="未入力の場合は自動生成されます"
                  {...register('bioNumber')}
                />
                <Text fontSize="xs" color="gray.600" mt={1}>
                  例: DOG-2025-001
                </Text>
                {errors.bioNumber && (
                  <Text fontSize="sm" color="red.500" mt={1}>
                    {errors.bioNumber.message}
                  </Text>
                )}
              </Box>

              {/* 名前 */}
              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={2}>
                  名前 <Text as="span" color="red.500">*</Text>
                </Text>
                <Input
                  type="text"
                  placeholder="花子"
                  {...register('name', { required: '名前を入力してください' })}
                />
                {errors.name && (
                  <Text fontSize="sm" color="red.500" mt={1}>
                    {errors.name.message}
                  </Text>
                )}
              </Box>

              {/* 性別 */}
              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={2}>
                  性別 <Text as="span" color="red.500">*</Text>
                </Text>
                <NativeSelectRoot>
                  <NativeSelectField {...register('gender', { required: true })}>
                    <option value="">選択してください</option>
                    <option value="female">メス</option>
                    <option value="male">オス</option>
                  </NativeSelectField>
                </NativeSelectRoot>
                {errors.gender && (
                  <Text fontSize="sm" color="red.500" mt={1}>
                    性別を選択してください
                  </Text>
                )}
              </Box>

              {/* 出生日 */}
              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={2}>
                  出生日 <Text as="span" color="red.500">*</Text>
                </Text>
                <Input
                  type="date"
                  {...register('birthDateStr', { required: '出生日を選択してください' })}
                />
                {errors.birthDateStr && (
                  <Text fontSize="sm" color="red.500" mt={1}>
                    {errors.birthDateStr.message}
                  </Text>
                )}
              </Box>

              {/* 血統情報 */}
              <Heading size="md" mt={4}>血統情報（任意）</Heading>

              {/* 父犬選択 */}
              <ParentDogSelector
                dogs={dogs}
                gender="male"
                value={fatherId}
                onChange={(value) => setValue('fatherId', value)}
                label="父犬（オス）"
                helperText="父犬を選択すると血統図に表示されます"
                excludeId={id}
                allowCreate
                matchResult={fatherMatchResult}
              />

              {/* 父方祖父母（父犬直下に階層表示） */}
              <Box pl={6} borderLeft="2px solid" borderColor="gray.200">
                <VStack gap={4} align="stretch">
                  <ParentDogSelector
                    dogs={dogs}
                    gender="male"
                    value={paternalGrandfatherId}
                    onChange={(value) => setValue('paternalGrandfatherId', value)}
                    label="父方祖父（オス）"
                    excludeId={id}
                    allowCreate
                    matchResult={paternalGrandfatherMatchResult}
                  />
                  <ParentDogSelector
                    dogs={dogs}
                    gender="female"
                    value={paternalGrandmotherId}
                    onChange={(value) => setValue('paternalGrandmotherId', value)}
                    label="父方祖母（メス）"
                    excludeId={id}
                    allowCreate
                    matchResult={paternalGrandmotherMatchResult}
                  />
                </VStack>
              </Box>

              {/* 母犬選択 */}
              <ParentDogSelector
                dogs={dogs}
                gender="female"
                value={motherId}
                onChange={(value) => setValue('motherId', value)}
                label="母犬（メス）"
                helperText="母犬を選択すると血統図に表示されます"
                excludeId={id}
                allowCreate
                matchResult={motherMatchResult}
              />

              {/* 母方祖父母（母犬直下に階層表示） */}
              <Box pl={6} borderLeft="2px solid" borderColor="gray.200">
                <VStack gap={4} align="stretch">
                  <ParentDogSelector
                    dogs={dogs}
                    gender="male"
                    value={maternalGrandfatherId}
                    onChange={(value) => setValue('maternalGrandfatherId', value)}
                    label="母方祖父（オス）"
                    excludeId={id}
                    allowCreate
                    matchResult={maternalGrandfatherMatchResult}
                  />
                  <ParentDogSelector
                    dogs={dogs}
                    gender="female"
                    value={maternalGrandmotherId}
                    onChange={(value) => setValue('maternalGrandmotherId', value)}
                    label="母方祖母（メス）"
                    excludeId={id}
                    allowCreate
                    matchResult={maternalGrandmotherMatchResult}
                  />
                </VStack>
              </Box>

              {/* ステータス */}
              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={2}>
                  ステータス <Text as="span" color="red.500">*</Text>
                </Text>
                <NativeSelectRoot>
                  <NativeSelectField {...register('status', { required: true })}>
                    <option value="">選択してください</option>
                    <option value="active">在籍中</option>
                    <option value="transferred">譲渡済み</option>
                    <option value="deceased">死亡</option>
                  </NativeSelectField>
                </NativeSelectRoot>
                {errors.status && (
                  <Text fontSize="sm" color="red.500" mt={1}>
                    ステータスを選択してください
                  </Text>
                )}
              </Box>

              {/* 登録情報セクション */}
              <Heading size="md" mt={4}>登録情報（任意）</Heading>
              <Text fontSize="xs" color="gray.500" mb={2}>
                血統書やマイクロチップ証明書から自動取得できます
              </Text>

              {/* 犬種 */}
              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={2}>
                  犬種
                </Text>
                <Input
                  type="text"
                  placeholder="例: トイプードル"
                  {...register('breed')}
                />
              </Box>

              {/* 正式名称 */}
              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={2}>
                  正式名称（血統書名）
                </Text>
                <Input
                  type="text"
                  placeholder="血統書に記載の正式名称"
                  {...register('officialName')}
                />
              </Box>

              {/* マイクロチップ番号 */}
              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={2}>
                  マイクロチップ番号
                </Text>
                <Input
                  type="text"
                  placeholder="15桁の数字"
                  {...register('microchipNumber')}
                />
                <Text fontSize="xs" color="gray.600" mt={1}>
                  例: 392141234567890
                </Text>
              </Box>

              {/* マイクロチップ暗証記号 */}
              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={2}>
                  マイクロチップ暗証記号
                </Text>
                <Input
                  type="text"
                  placeholder="暗証記号"
                  {...register('microchipPin')}
                />
              </Box>

              {/* JKC登録番号 */}
              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={2}>
                  JKC登録番号
                </Text>
                <Input
                  type="text"
                  placeholder="JKC登録番号"
                  {...register('jkcRegistrationNumber')}
                />
              </Box>

              {/* DNA登録番号 */}
              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={2}>
                  DNA登録番号
                </Text>
                <Input
                  type="text"
                  placeholder="DNA登録番号"
                  {...register('dnaRegistrationNumber')}
                />
              </Box>

              {/* 繁殖者名 */}
              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={2}>
                  繁殖者名
                </Text>
                <Input
                  type="text"
                  placeholder="繁殖者名"
                  {...register('breederName')}
                />
              </Box>

              {/* 所有者名 */}
              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={2}>
                  所有者名
                </Text>
                <Input
                  type="text"
                  placeholder="所有者名"
                  {...register('ownerName')}
                />
              </Box>

              {/* 遺伝子検査結果セクション */}
              {geneticTestResults.length > 0 && (
                <Box>
                  <Heading size="md" mt={4} mb={3}>遺伝子検査結果</Heading>
                  <VStack gap={2} align="stretch">
                    {geneticTestResults.map((result, index) => (
                      <HStack
                        key={index}
                        p={3}
                        bg="gray.50"
                        borderRadius="md"
                        justify="space-between"
                      >
                        <HStack gap={3}>
                          <Text fontWeight="medium" fontSize="sm">
                            {result.testName}
                          </Text>
                          <Badge colorPalette={GENETIC_TEST_RESULT_COLORS[result.result]}>
                            {GENETIC_TEST_RESULT_LABELS[result.result]}
                          </Badge>
                        </HStack>
                        <Button
                          size="xs"
                          variant="ghost"
                          colorPalette="red"
                          onClick={() => handleRemoveGeneticTestResult(index)}
                        >
                          <FiX />
                        </Button>
                      </HStack>
                    ))}
                  </VStack>
                  <Text fontSize="xs" color="gray.500" mt={2}>
                    遺伝子検査結果報告書から自動取得されました
                  </Text>
                </Box>
              )}

              {/* 備考 */}
              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={2}>
                  備考
                </Text>
                <Textarea
                  rows={4}
                  placeholder="特記事項があれば記入してください"
                  {...register('notes')}
                />
              </Box>

              {/* 保存ボタン */}
              <Button type="submit" colorPalette="blue" w="100%" loading={isLoading}>
                {isLoading ? '保存中...' : isEditMode ? '更新' : '登録'}
              </Button>
            </VStack>
          </form>
        </Box>

        {/* ワクチン接種記録セクション（保存後または編集モード時） */}
        {(showPhotoSection || (isEditMode && id)) && savedDogId && user && (
          <VaccinationSection dogId={savedDogId} userId={user.uid} />
        )}

        {/* 写真セクション（保存後または編集モード時） */}
        {(showPhotoSection || (isEditMode && id)) && savedDogId && user && (
          <>
            {/* 既存の写真（編集モード時のみ） */}
            {isEditMode && id && (
              <Box bg="white" p={8} borderRadius="lg" shadow="md">
                <VStack gap={4} align="stretch">
                  <Heading size="md">登録済み写真</Heading>
                  <PhotoGallery dogId={savedDogId} userId={user.uid} />
                </VStack>
              </Box>
            )}

            {/* 写真アップロード */}
            <Box bg="white" p={8} borderRadius="lg" shadow="md">
              <VStack gap={4} align="stretch">
                <Heading size="md">写真をアップロード</Heading>
                <Text fontSize="sm" color="gray.600">
                  {isEditMode ? '追加の写真をアップロードできます' : '最初の写真が自動的にメイン写真に設定されます'}
                </Text>
                <PhotoUploader
                  dogId={savedDogId}
                  userId={user.uid}
                  onUploadComplete={handlePhotoUploadComplete}
                />
              </VStack>
            </Box>

            {/* スキップボタン */}
            <Button
              w="100%"
              variant="outline"
              onClick={() => {
                toaster.create({
                  title: '登録が完了しました',
                  description: '個体情報が正常に保存されました',
                  type: 'success',
                  duration: 4000,
                });
                navigate('/dogs');
              }}
            >
              {isEditMode ? '完了' : '写真アップロードをスキップ'}
            </Button>
          </>
        )}
        </VStack>
      </Box>
    </AppLayout>
  );
};

export default DogFormPage;
